import React from 'react';
import { Container, Typography, Box, Paper, Divider } from '@mui/material';

const TermsAndConditions = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
          TERMS AND CONDITIONS AGREEMENT
        </Typography>
        <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#555', fontWeight: 600 }}>
          Prarthana: Pray for Others
        </Typography>

        <Box sx={{ my: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
          <Typography variant="body2" sx={{ color: '#333' }}>
            <strong>Operated by:</strong> Maverick Thinkers Pvt Ltd.
          </Typography>
          <Typography variant="body2" sx={{ color: '#333' }}>
            <strong>Effective Date:</strong> 23 October 2026
          </Typography>
          <Typography variant="body2" sx={{ color: '#333' }}>
            <strong>Last Updated:</strong> 23 October 2026
          </Typography>
          <Typography variant="body2" sx={{ color: '#333' }}>
            <strong>Registered Office Address:</strong> 201, Shri Kalyanam Residency, Nandlalpura Main Road, Indore
          </Typography>
          <Typography variant="body2" sx={{ color: '#333' }}>
            <strong>Official Support Email:</strong> <a href="mailto:alok@maverickthinkers.in" style={{ color: '#1976d2' }}>alok@maverickthinkers.in</a>
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" paragraph>
          Welcome to Prarthana: Pray for Others (“Prarthana,” “the App,” “we,” “us,” or “our”), operated by Maverick Thinkers Pvt Ltd.
        </Typography>
        <Typography variant="body1" paragraph>
          Prarthana is a platform where people can request prayers for others and participate in collective prayer for people going through difficult situations.
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms and Conditions explain the rules for using the App. By creating an account or using Prarthana, you agree to these Terms.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: '#d32f2f' }}>
          If you do not agree with these Terms, please do not use the App.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            1. Purpose of Prarthana
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana has been created to encourage selfless prayer, kindness, compassion, and spiritual support.
          </Typography>
          <Typography variant="body1" paragraph>
            Users can post prayer requests for people facing illness, mental health challenges, medical procedures, personal difficulties, or other challenging situations.
          </Typography>
          <Typography variant="body1" paragraph>
            Other users can view these requests and choose to pray according to their own faith and beliefs.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana welcomes people from different religions, communities, and backgrounds, with a special focus on children and people facing mental health challenges.
          </Typography>
          <Typography variant="body1" paragraph>
            The App is intended for prayer and spiritual support. It is not a medical service, fundraising platform, counselling service, or emergency assistance service.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            2. Eligibility and Accounts
          </Typography>
          <Typography variant="body1" paragraph>
            People of any age may use Prarthana, subject to applicable law and the safeguards required for children.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must provide accurate information when creating an account and must not impersonate another person.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must keep their account credentials secure and must not misuse another person's account.
          </Typography>
          <Typography variant="body1" paragraph>
            Where required by applicable law, a parent, legal guardian, or other legally authorised person must provide the necessary consent for a child to use the App or for the processing or sharing of the child's personal information.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana may restrict access where necessary to protect children, other users, or the platform.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            3. How the App Works
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana allows users to:
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 2 }}>
            <li>Submit prayer requests for people in need.</li>
            <li>View prayer requests submitted by other users.</li>
            <li>Express their willingness to participate in prayer by commenting on a request.</li>
            <li>Pray according to their own religious beliefs and practices.</li>
            <li>Participate in collective prayer for the well-being of others.</li>
          </Box>
          <Typography variant="body1" paragraph>
            Users may recite the Hanuman Chalisa, chant a mantra, read a shloka, or follow another prayer practice consistent with their faith.
          </Typography>
          <Typography variant="body1" paragraph>
            No user is required to follow a particular religion or prayer method.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            4. Collective Prayer and the 216-Participant Target
          </Typography>
          <Typography variant="body1" paragraph>
            Each prayer request can receive up to 216 expressions of willingness to participate.
          </Typography>
          <Typography variant="body1" paragraph>
            The target is inspired by the traditional spiritual significance of 108 recitations in Hindu devotional practice. The additional participants provide a buffer in case some people are unable to complete their intended prayers.
          </Typography>
          <Typography variant="body1" paragraph>
            The aim is to encourage at least 108 people to pray for someone in need.
          </Typography>
          <Typography variant="body1" paragraph>
            However:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Expressing willingness to participate does not guarantee that a person will complete the prayer.</li>
            <li>Prarthana cannot independently verify that every participant has completed their prayer.</li>
            <li>The App does not guarantee that a specific number of prayers will be completed.</li>
            <li>Participation does not guarantee healing, recovery, financial improvement, or any other outcome.</li>
          </Box>
          <Typography variant="body1" paragraph>
            The target is intended to encourage collective participation, not to promise a particular result.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            5. Respect for All Religions and Beliefs
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana welcomes people of all religions, faiths, and spiritual traditions.
          </Typography>
          <Typography variant="body1" paragraph>
            Users are free to pray according to their own beliefs.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must not:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Insult or attack any religion, faith, deity, or religious practice.</li>
            <li>Spread hatred against any religious or social community.</li>
            <li>Force others to follow a particular belief or prayer method.</li>
            <li>Harass or disrespect people because of their faith.</li>
            <li>Use the App to start religious arguments or disputes unrelated to prayer.</li>
          </Box>
          <Typography variant="body1" paragraph>
            Every user must treat others with dignity and respect.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            6. Consent Before Sharing Personal Information
          </Typography>
          <Typography variant="body1" paragraph>
            Respecting privacy is a basic requirement for using Prarthana.
          </Typography>
          <Typography variant="body1" paragraph>
            Before posting a prayer request about another person, you must obtain the necessary permission to share their personal information.
          </Typography>
          <Typography variant="body1" paragraph>
            When submitting a prayer request, you must confirm that you have the required permission and legal authority to share the information, photographs, and other materials included in the request.
          </Typography>
          <Typography variant="body1" paragraph>
            Do not share another person's name, age, address, photograph, medical condition, mental health information, contact details, medical reports, or other private information without appropriate permission.
          </Typography>
          <Typography variant="body1" paragraph>
            Only share information that is necessary for the prayer request.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Special care must be taken when posting information about children or people who may be unable to provide informed consent.</strong> Obtain the appropriate permission from a parent, legal guardian, or other legally authorized person, as applicable. Do not assume that being related to someone automatically gives you permission to share their private information.
          </Typography>
          <Typography variant="body1" paragraph>
            If you do not have the required permission, do not publish the person's identifying or sensitive information.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana may remove content that violates these privacy requirements.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            7. Rules for Posting Prayer Requests
          </Typography>
          <Typography variant="body1" paragraph>
            When submitting a prayer request, you confirm that:
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 2 }}>
            <li>The request is genuine and intended for prayer or spiritual support.</li>
            <li>The information you provide is accurate to the best of your knowledge.</li>
            <li>You have obtained the necessary permission to share another person's information.</li>
            <li>You have the rights and permissions required to upload any photographs or other materials.</li>
            <li>You will not publish unnecessary personal or sensitive information.</li>
            <li>You will not use another person's difficult situation to harass, threaten, embarrass, or exploit them.</li>
          </Box>
          <Typography variant="body1" paragraph>
            You are responsible for the information and materials you submit.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana may reject, restrict, or remove a request if it violates these Terms, applicable law, or another person's privacy or safety.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            8. Special Protection for Children and People Facing Mental Health Challenges
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana has a special focus on children and people experiencing mental health challenges.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must treat these individuals with care, dignity, and respect.
          </Typography>
          <Typography variant="body1" paragraph>
            You must not:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Share a child's personal or medical information without the necessary permission.</li>
            <li>Publish photographs of children without appropriate consent.</li>
            <li>Shame, threaten, or make fun of someone because of a mental health condition.</li>
            <li>Share private medical or mental health records without proper authorization.</li>
            <li>Make misleading claims about someone's health or circumstances.</li>
            <li>Use a person's vulnerability to obtain money, private information, or other benefits.</li>
          </Box>
          <Typography variant="body1" paragraph>
            Prarthana is not a crisis intervention service. If someone appears to be in immediate danger, contact appropriate emergency services or qualified professionals.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            9. No Fundraising or Requests for Money
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana is not a donation or fundraising platform.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must not use prayer requests, comments, profiles, or other features to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Ask for money or donations.</li>
            <li>Collect funds for medical treatment or personal expenses.</li>
            <li>Request loans or financial assistance.</li>
            <li>Share payment links, bank details, or fundraising links.</li>
            <li>Promote fundraising campaigns.</li>
          </Box>
          <Typography variant="body1" paragraph>
            Users must not use another person's illness or difficult situation to solicit money through the App.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana may remove such content and restrict or terminate accounts that violate this rule.
          </Typography>
          <Typography variant="body1" paragraph>
            This restriction applies to fundraising by users. It does not prevent Maverick Thinkers Pvt Ltd. from earning legitimate revenue through permitted advertising or other disclosed business activities.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            10. Advertising and Revenue
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana is created to encourage collective prayer and spiritual support. However, operating and maintaining the App may involve costs.
          </Typography>
          <Typography variant="body1" paragraph>
            To help cover these costs, Maverick Thinkers Pvt Ltd. may introduce advertisements within the App in the future.
          </Typography>
          <Typography variant="body1" paragraph>
            The Company may earn revenue or profit from such advertisements and other permitted business activities.
          </Typography>
          <Typography variant="body1" paragraph>
            The introduction of advertising does not change the core purpose of Prarthana.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana will not operate as a platform for users to raise donations or collect money for individuals through prayer requests.
          </Typography>
          <Typography variant="body1" paragraph>
            Any advertising introduced in the App will be subject to applicable law and relevant platform policies.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            11. Prohibited Content and Behavior
          </Typography>
          <Typography variant="body1" paragraph>
            Users must not post or share content that:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Contains threats, harassment, bullying, or abuse.</li>
            <li>Promotes hatred, discrimination, or violence.</li>
            <li>Contains sexually explicit, obscene, or exploitative material.</li>
            <li>Promotes illegal activities.</li>
            <li>Contains misleading or fraudulent claims.</li>
            <li>Violates another person's privacy or intellectual property rights.</li>
            <li>Promotes unrelated commercial activities or political campaigns.</li>
            <li>Misuses the App for purposes other than its intended use.</li>
          </Box>
          <Typography variant="body1" paragraph>
            Users must not impersonate others, create misleading accounts, or attempt to disrupt or damage the App.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana may take action against users who violate these rules.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            12. Comments and User-Generated Content
          </Typography>
          <Typography variant="body1" paragraph>
            Users are responsible for the content they submit, including prayer requests, comments, photographs, and other materials.
          </Typography>
          <Typography variant="body1" paragraph>
            Comments should remain respectful and focused on prayer.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must not use comments to argue, insult others, disclose private information, solicit money, or post unrelated material.
          </Typography>
          <Typography variant="body1" paragraph>
            A comment expressing willingness to pray represents the user's stated intention. It does not verify that the prayer has been completed.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana does not necessarily endorse or verify every statement made by users.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            13. Manual Review, Reporting, and Account Bans
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana intends to manually review prayer requests during its initial stages, while the volume of requests remains manageable.
          </Typography>
          <Typography variant="body1" paragraph>
            As the platform grows, the review process may be adjusted to accommodate the increased volume while maintaining appropriate safeguards.
          </Typography>
          <Typography variant="body1" paragraph>
            Users may report content that violates these Terms, exposes private information, appears fraudulent, or creates a safety concern through the reporting feature or the official contact details provided by Prarthana.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana may review reported content and take appropriate action, including:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Rejecting or removing a prayer request.</li>
            <li>Removing inappropriate comments or photographs.</li>
            <li>Restricting access to certain features.</li>
            <li>Temporarily suspending an account.</li>
            <li>Permanently banning a user from the platform.</li>
          </Box>
          <Typography variant="body1" paragraph>
            <strong>Users who repeatedly violate these Terms or the platform's rules may be permanently banned from using Prarthana.</strong>
          </Typography>
          <Typography variant="body1" paragraph>
            Serious violations, including misuse of children's information, harassment, fraudulent activity, or conduct that puts someone at risk, may result in immediate action without waiting for repeated violations.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana may take action without prior notice where necessary to protect users, privacy, or platform safety, subject to applicable law.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            14. Medical and Mental Health Disclaimer
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana provides a space for prayer and spiritual support.
          </Typography>
          <Typography variant="body1" paragraph>
            Prayer may be personally meaningful and may provide comfort, hope, connection, or peace. However, the App does not claim that prayer will cure an illness or produce a particular result.
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana does not provide medical diagnosis, medical treatment, mental health therapy, professional counselling, or emergency medical assistance.
          </Typography>
          <Typography variant="body1" paragraph>
            Prayer must not replace prescribed medication, medical treatment, professional mental health care, or advice from qualified healthcare professionals.
          </Typography>
          <Typography variant="body1" paragraph>
            Users should consult appropriate professionals for medical and mental health concerns.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            15. No Guarantee of Outcomes
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana is built on the belief that sincere prayer and concern for others are valuable expressions of faith and compassion.
          </Typography>
          <Typography variant="body1" paragraph>
            However, every person's circumstances are different.
          </Typography>
          <Typography variant="body1" paragraph>
            We do not guarantee that using the App, participating in collective prayer, or receiving prayers from other users will result in recovery, financial prosperity, relief from difficulties, or any specific outcome.
          </Typography>
          <Typography variant="body1" paragraph>
            Participation is voluntary and based on personal faith.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            16. The Principle of Karma
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana is inspired by the principle of karma and the idea that our actions and intentions matter.
          </Typography>
          <Typography variant="body1" paragraph>
            Users are encouraged to wish for others the same health, happiness, prosperity, and well-being that they wish for themselves.
          </Typography>
          <Typography variant="body1" paragraph>
            This philosophy is intended to encourage kindness and selfless service.
          </Typography>
          <Typography variant="body1" paragraph>
            It does not mean that a person's illness, disability, financial hardship, or other difficulties are their own fault. Prarthana does not support blaming or judging anyone for their circumstances.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            17. Privacy and Personal Data
          </Typography>
          <Typography variant="body1" paragraph>
            Prarthana respects the privacy of its users.
          </Typography>
          <Typography variant="body1" paragraph>
            The App may collect or process information necessary to provide its services, depending on the features used and the permissions granted.
          </Typography>
          <Typography variant="body1" paragraph>
            The information collected, why it is collected, how it is used, how long it is retained, and when it may be shared will be explained in the separate Privacy Policy.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must read the Privacy Policy before using the App.
          </Typography>
          <Typography variant="body1" paragraph>
            Personal information will be handled in accordance with the Privacy Policy and applicable data protection laws.
          </Typography>
          <Typography variant="body1" paragraph>
            Content displayed publicly or made available to other users should not be assumed to be private.
          </Typography>
          <Typography variant="body1" paragraph>
            Users should not submit information they are not authorised to share.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            18. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            The App's name, logo, original design, text, graphics, and other materials owned by Prarthana or its operator are protected by applicable intellectual property laws.
          </Typography>
          <Typography variant="body1" paragraph>
            Users may not copy, reproduce, distribute, or commercially use these materials without permission, except where permitted by law.
          </Typography>
          <Typography variant="body1" paragraph>
            Users retain any rights they have in content they submit. By submitting content, users grant Prarthana permission to host, display, store, and process it as reasonably necessary to operate the App and provide its features, subject to the Privacy Policy and applicable law.
          </Typography>
          <Typography variant="body1" paragraph>
            Users must have the necessary rights and permissions for all photographs, text, and other materials they upload.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            19. Availability of the App
          </Typography>
          <Typography variant="body1" paragraph>
            We aim to keep Prarthana available and working properly. However, uninterrupted access or continuous availability of every feature cannot be guaranteed.
          </Typography>
          <Typography variant="body1" paragraph>
            Maverick Thinkers Pvt Ltd. may update, change, suspend, or discontinue features to maintain, improve, or protect the App, subject to applicable law.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            20. Limitation of Responsibility
          </Typography>
          <Typography variant="body1" paragraph>
            To the extent permitted by applicable law, Prarthana and its operator are not responsible for:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>The accuracy or completeness of information submitted by users.</li>
            <li>Whether users complete prayers after expressing willingness to participate.</li>
            <li>Medical, financial, or other outcomes associated with prayer requests.</li>
            <li>Interactions between users outside the App.</li>
            <li>Disruptions caused by circumstances beyond reasonable control.</li>
          </Box>
          <Typography variant="body1" paragraph>
            Nothing in these Terms excludes or limits any liability that cannot legally be excluded or limited under applicable law.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            21. Changes to These Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We may update these Terms to reflect changes in the App, its features, our practices, or applicable law.
          </Typography>
          <Typography variant="body1" paragraph>
            The updated version will be made available through the App or another appropriate channel, with the effective date stated where applicable.
          </Typography>
          <Typography variant="body1" paragraph>
            Where required by law, we will provide notice or obtain consent before changes take effect.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            22. Suspension or Termination
          </Typography>
          <Typography variant="body1" paragraph>
            Users may stop using Prarthana at any time.
          </Typography>
          <Typography variant="body1" paragraph>
            Maverick Thinkers Pvt Ltd. may suspend or terminate access where a user violates these Terms, misuses the platform, threatens the safety or privacy of others, or engages in conduct that requires action under applicable law.
          </Typography>
          <Typography variant="body1" paragraph>
            Account deletion and the handling of associated personal information will be governed by the App's account deletion process, Privacy Policy, and applicable law.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            23. Applicable Law and Disputes
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms will be governed by the laws of India, subject to applicable legal requirements.
          </Typography>
          <Typography variant="body1" paragraph>
            Disputes arising from the use of Prarthana will be handled in accordance with applicable Indian law and the jurisdiction of competent courts, subject to mandatory legal rights and protections.
          </Typography>
          <Typography variant="body1" paragraph>
            Nothing in these Terms removes any legal rights that users cannot waive under applicable law.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            24. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            For questions about these Terms, reports of violations, or privacy concerns, please contact:
          </Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>App Name:</strong> Prarthana: Pray for Others
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Operator:</strong> Maverick Thinkers Pvt Ltd.
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Official Support Email:</strong> <a href="mailto:alok@maverickthinkers.in" style={{ color: '#1976d2' }}>alok@maverickthinkers.in</a>
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Registered Office Address:</strong> 201, Shri Kalyanam Residency, Nandlalpura Main Road, Indore
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3 }} gutterBottom>
            25. Final Acknowledgement
          </Typography>
          <Typography variant="body1" paragraph>
            By creating an account or using Prarthana, you acknowledge that:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            <li>You understand the purpose of the App.</li>
            <li>You will respect the privacy, dignity, and beliefs of others.</li>
            <li>You will obtain the necessary permission before sharing another person's personal information.</li>
            <li>You will not use the App for fundraising, harassment, discrimination, or prohibited activities.</li>
            <li>You understand that Prarthana may display advertisements in the future.</li>
            <li>You understand that the App provides a platform for prayer and spiritual support, not medical treatment or guaranteed outcomes.</li>
          </Box>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#555', fontStyle: 'italic' }}>
              Thank you for being part of Prarthana.
            </Typography>
            <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold', color: '#1976d2' }}>
              Pray for Others. Care for Others. Make Kindness a Part of Your Life.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsAndConditions;
